<script setup lang="ts">
import { onClickOutside, useEventListener } from '@vueuse/core'

import CustomVarInput from './CustomVarInput.vue'

import { buildResolvedCustomVarCode, extractCustomValue, findInVarListFromCode, isInVarList } from './utils'

import type { Icons } from '@/components/base/BaseIcon.vue'
import type { Survey } from '@/types/types'
import type { CustomVars, I18nStrings } from './interfaces'

const dropdownCategories: ReadonlyArray<{ type: 'variable' | 'link' | 'webapp'; icon: Icons }> = [
  { type: 'variable', icon: 'HeroVariableIconSolid' },
  { type: 'webapp', icon: 'HeroGlobeAltIconSolid' },
  { type: 'link', icon: 'HeroLinkIconSolid' }
]

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    customVars: CustomVars[]
    label: string
    i18nStrings: I18nStrings
    textareaPosition: {
      left: number
      width: number
    }
    type?: 'insert' | 'update'
    surveys?: Survey[]
    isLoadingSurveys?: boolean
    loadSurveysError?: boolean
    requiredStoreIds?: number[]
  }>(),
  {
    type: 'insert'
  }
)

const emit = defineEmits<{
  (e: 'varClicked', code: string): void
}>()

const isOpen = ref(false)
const showCustomValueInput = ref(false)
const customValue = ref('')
const editingCustomVar = ref<CustomVars | null>(null)
const filterSearch = ref('')
const isNestedSelectOpen = ref(false)
let wasNestedSelectOpenOnPointerDown = false

const categoriesWithVars = computed(() => {
  return dropdownCategories.filter((category) => {
    return props.customVars.some((customVar) => customVar.type === category.type)
  })
})

useEventListener(
  window,
  'pointerdown',
  () => {
    // Snapshot "select was open" without triggering reactivity on every click.
    wasNestedSelectOpenOnPointerDown = isNestedSelectOpen.value
  },
  { capture: true }
)

const handleVarClicked = (customVar: CustomVars, event: MouseEvent) => {
  if (customVar?.disabled === true) return
  if (customVar?.customValueType) {
    event.preventDefault()
    event.stopPropagation()
    showCustomValueInput.value = true
    editingCustomVar.value = customVar
  } else {
    emit('varClicked', customVar.code)
    closeDropdown()
  }
}

const dropdownOffsetLeft = ref<number>(0)
const dropdownMenu = ref(null)
const dropdownPositionClass = ref<string>('')
const dropdownVarType = ref<'variable' | 'link' | 'webapp' | null>(null)

const filteredCustomVars = computed(() => {
  if (dropdownVarType.value === null) return []
  return props.customVars.filter((customVar) => customVar.type === dropdownVarType.value)
})

const activeCategoryIcon = computed(() => {
  if (dropdownVarType.value === null) return undefined
  return dropdownCategories.find((category) => category.type === dropdownVarType.value)?.icon
})

const labelTextClass = computed(() => {
  if (props.type !== 'update') return ''

  const found = findInVarListFromCode(props.label, props.customVars)
  const isValidVar = isInVarList({
    varCode: props.label,
    customVars: props.customVars,
    surveys: props.surveys,
    requiredStoreIds: props.requiredStoreIds
  })

  const skipMissingSurveyError = props.surveys === null && props.label?.startsWith('customerApp(survey/')

  if ((isValidVar && !found?.disabled) || skipMissingSurveyError) return 'text-blue-500'
  if (isValidVar && found?.disabled) return 'text-yellow-500'

  return 'text-red-500'
})

/** `null` when search is empty; otherwise all variables matching the query (any category). */
const searchFilteredVars = computed((): CustomVars[] | null => {
  const q = filterSearch.value.trim().toLowerCase()
  if (!q) return null
  return props.customVars.filter(
    (customVar) => customVar.label.toLowerCase().includes(q) || customVar.code.toLowerCase().includes(q)
  )
})

const setOffsetDropdown = (event: MouseEvent) => {
  isOpen.value = true

  if (props.type === 'update') {
    const code = props.label.replace(/\/\d+/g, '').replace(/customLink\((.*)\)/g, 'customLink')

    const existingCustomVar = props.customVars.find((customVar) => customVar.code === code)

    dropdownVarType.value = existingCustomVar?.type ?? 'variable'

    if (existingCustomVar?.customValueType === 'link' || existingCustomVar?.customValueType === 'pageId') {
      checkForCustomValue()
    }
  } else {
    dropdownVarType.value = null
  }

  filterSearch.value = ''

  const elementLeftPosition = (event.target as HTMLElement).getBoundingClientRect().left - props.textareaPosition.left
  let elementWidth = (event.target as HTMLElement).getBoundingClientRect().width
  const buttonRect = (event.target as HTMLElement).getBoundingClientRect()
  const defaultTopClass = props.type === 'update' ? 'top-6' : 'top-8'

  nextTick(() => {
    if (dropdownMenu.value) {
      const dropdownWidth = dropdownMenu.value.getBoundingClientRect().width
      const dropdownHeight = dropdownMenu.value.getBoundingClientRect().height

      if (props.type === 'update') elementWidth += 10

      // Horizontal positioning
      if (elementLeftPosition + dropdownWidth > props.textareaPosition.width) {
        dropdownOffsetLeft.value = elementWidth - dropdownWidth
      } else {
        dropdownOffsetLeft.value = 0
      }

      // Vertical positioning: check if dropdown would overflow bottom of viewport
      const buttonBottom = buttonRect.bottom
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - buttonBottom

      if (spaceBelow < dropdownHeight) {
        // Not enough space below, show at top of button
        dropdownPositionClass.value = props.type === 'update' ? 'bottom-6' : 'bottom-8'
      } else {
        // Default: show at bottom of button
        dropdownPositionClass.value = defaultTopClass
      }
    }
  })
}

const handleCustomValue = () => {
  if (!editingCustomVar.value) return
  emit('varClicked', buildResolvedCustomVarCode(editingCustomVar.value, customValue.value))
  closeDropdown()
}

onClickOutside(dropdownMenu, () => {
  // If the Select was open when the pointer went down, we let the Select close,
  // but we keep this DropdownVars open for that interaction.
  if (wasNestedSelectOpenOnPointerDown) {
    wasNestedSelectOpenOnPointerDown = false
    return
  }
  closeDropdown()
})

const closeDropdown = () => {
  isOpen.value = false
  showCustomValueInput.value = false
  editingCustomVar.value = null
  customValue.value = ''
  filterSearch.value = ''
  dropdownPositionClass.value = ''
  isNestedSelectOpen.value = false
}

const isSelected = (customVar: CustomVars) => {
  return (
    props.label &&
    props.type === 'update' &&
    findInVarListFromCode(props.label, props.customVars)?.code === customVar.code
  )
}

const checkForCustomValue = () => {
  const { value, customVar } = extractCustomValue(props.label, props.customVars)

  if (customVar?.customValueType === 'pageId') {
    customValue.value = String(value ?? '')
    showCustomValueInput.value = true
    editingCustomVar.value = customVar
    return
  }

  if (value && customVar) {
    customValue.value = value
    showCustomValueInput.value = true
    editingCustomVar.value = customVar
  }
}
</script>

<template>
  <span class="cursor-pointer border-transparent bg-transparent py-0" @click="setOffsetDropdown">
    <span
      v-if="type === 'insert'"
      class="mt-2 inline-flex cursor-pointer flex-row items-center text-sm font-medium text-blue-600 hover:underline"
    >
      <BaseIcon name="HeroVariableIconSolid" class="mr-1 h-4 w-4" />{{ label }}
    </span>

    <span v-if="type === 'update'" spellcheck="false" :class="labelTextClass">
      {{ label }}
    </span>
  </span>

  <Transition
    leave-active-class="transition ease-in duration-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <ul
      v-if="isOpen"
      ref="dropdownMenu"
      class="absolute z-50 max-h-80 w-80 overflow-auto rounded-control border bg-white text-base shadow-lg ring-0 focus:outline-hidden sm:text-sm"
      :class="dropdownPositionClass || (type === 'update' ? 'top-6' : 'top-8')"
      :style="{ left: `${dropdownOffsetLeft}px` }"
    >
      <CustomVarInput
        v-if="showCustomValueInput"
        v-model="customValue"
        v-model:select-open="isNestedSelectOpen"
        :value-type="editingCustomVar?.customValueType"
        :custom-value-model="editingCustomVar?.customValueModel"
        :surveys="surveys"
        :is-loading-surveys="isLoadingSurveys"
        :load-surveys-error="loadSurveysError"
        :i18n-strings="i18nStrings"
        :is-editing="type === 'update'"
        :required-store-ids="requiredStoreIds"
        @save="handleCustomValue"
        @back="showCustomValueInput = false"
      />

      <template v-else>
        <div class="px-4 py-1 flex flex-row items-center gap-2 border-b border-border">
          <BaseIcon
            name="HeroMagnifyingGlassIconSolid"
            class="size-5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            v-model="filterSearch"
            class="border-none! p-0! flex-1 ring-none! shadow-none! focus:ring-0! focus:ring-offset-0!"
            :placeholder="t('global.words.search')"
          />
        </div>

        <template v-if="searchFilteredVars !== null">
          <li v-if="searchFilteredVars.length === 0" class="px-4 py-3 text-center text-sm">
            {{ t('global.words.noResult') }}
          </li>

          <template v-for="category in categoriesWithVars" v-else :key="category.type">
            <CategoryVarList
              :category-type="category.type"
              :icon="category.icon"
              :custom-vars="searchFilteredVars.filter((customVar) => customVar.type === category.type)"
              :i18n-strings="i18nStrings"
              sticky-top-class="top-0"
              :is-selected="isSelected"
              @var-clicked="handleVarClicked"
            />
          </template>
        </template>

        <li v-else-if="dropdownVarType === null" class="bg-white">
          <div
            v-for="cat in categoriesWithVars"
            :key="cat.type"
            class="cursor-pointer px-4 py-3"
            :class="{
              'bg-muted': dropdownVarType === cat.type,
              'hover:bg-muted/50': dropdownVarType !== cat.type
            }"
            @click="dropdownVarType = cat.type"
          >
            <div class="flex flex-row items-center gap-2">
              <div class="flex min-w-0 flex-1 flex-col">
                <div class="flex flex-row items-center text-blue-600">
                  <BaseIcon :name="cat.icon" class="size-5 shrink-0" aria-hidden="true" />
                  <span class="ml-2 block truncate text-base font-medium leading-5">
                    {{ t(`ui.textareaWithVars.dropdownVars.categories.${cat.type}.title`) }}
                  </span>
                </div>
                <p class="mt-1 text-sm text-foreground">
                  {{ t(`ui.textareaWithVars.dropdownVars.categories.${cat.type}.description`) }}
                </p>
              </div>
              <BaseIcon
                name="HeroChevronRightIconSolid"
                class="size-5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
            </div>
          </div>
        </li>

        <template v-else-if="dropdownVarType !== null">
          <li
            class="sticky top-0 bg-white cursor-pointer px-4 py-3 border-b flex flex-row items-center justify-between border-border hover:bg-muted/50 gap-2"
            @click="dropdownVarType = null"
          >
            <BaseIcon
              name="HeroChevronLeftIconSolid"
              class="size-5 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <p class="text-sm text-muted-foreground flex-1">
              {{ t('global.words.back') }}
            </p>
          </li>

          <CategoryVarList
            :category-type="dropdownVarType"
            :icon="activeCategoryIcon"
            :custom-vars="filteredCustomVars"
            :i18n-strings="i18nStrings"
            sticky-top-class="top-[45px]"
            :is-selected="isSelected"
            @var-clicked="handleVarClicked"
          />
        </template>
      </template>
    </ul>
  </Transition>
</template>
